import { useEffect, useState } from "react";
import { api, apiError, API } from "../lib/api";
import { PageHeader } from "../components/Layout";
import { Card } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { UsersThree, DeviceMobile, CurrencyInr, Plugs, ArrowsClockwise, Copy, CalendarBlank, Trash, CloudArrowUp, CheckCircle, WhatsappLogo, PaperPlaneTilt } from "@phosphor-icons/react";
import { Switch } from "../components/ui/switch";
import { toast } from "sonner";

const SHEET_NAME = "B2B Import format";

export default function Settings() {
  const [limit, setLimit] = useState(5);
  const [sellLimit, setSellLimit] = useState(5);
  const [busy, setBusy] = useState(false);
  const [busySell, setBusySell] = useState(false);
  const [feeAppliance, setFeeAppliance] = useState(1499);
  const [feeCleaning, setFeeCleaning] = useState(999);
  const [busyFees, setBusyFees] = useState(false);
  const [gToken, setGToken] = useState("");
  const [gBusy, setGBusy] = useState(false);
  const [slot, setSlot] = useState({ lead_days: 1, max_days: 14, slots: "", default_cap: 0, caps: [] });
  const [slotBusy, setSlotBusy] = useState(false);
  const [opsEmails, setOpsEmails] = useState("");
  const [opsBusy, setOpsBusy] = useState(false);
  const [paEnabled, setPaEnabled] = useState(false);
  const [paBusy, setPaBusy] = useState(false);
  const [drive, setDrive] = useState({ url: "", token: "", connected: false });
  const [driveInput, setDriveInput] = useState("");
  const [driveBusy, setDriveBusy] = useState(false);
  const [mig, setMig] = useState(null);
  const [migBusy, setMigBusy] = useState(false);
  const [wa, setWa] = useState(null);
  const [waBusy, setWaBusy] = useState(false);
  const [waTest, setWaTest] = useState({ phone: "", event: "order_receipt" });
  const [waTestBusy, setWaTestBusy] = useState(false);

  useEffect(() => { api.get("/settings").then((r) => {
    setLimit(r.data.partner_job_limit); setSellLimit(r.data.partner_sell_limit ?? 5);
    setFeeAppliance(r.data.onboarding_fee_appliance ?? 1499); setFeeCleaning(r.data.onboarding_fee_cleaning ?? 999);
    setSlot({ lead_days: r.data.booking_lead_days ?? 1, max_days: r.data.booking_max_days ?? 14,
      slots: (r.data.booking_time_slots || []).join("\n"), default_cap: r.data.booking_default_cap ?? 0,
      caps: r.data.booking_slot_caps || [] });
    setOpsEmails(r.data.ops_notification_emails || "");
    setPaEnabled(!!r.data.partner_availability_enabled);
  }).catch(() => {}); }, []);

  const slotList = slot.slots.split("\n").map((s) => s.trim()).filter(Boolean);
  const addCap = () => setSlot((s) => ({ ...s, caps: [...s.caps, { city: "", slot: slotList[0] || "", cap: 10 }] }));
  const updCap = (i, k, v) => setSlot((s) => ({ ...s, caps: s.caps.map((c, idx) => idx === i ? { ...c, [k]: v } : c) }));
  const rmCap = (i) => setSlot((s) => ({ ...s, caps: s.caps.filter((_, idx) => idx !== i) }));

  const saveSlots = async () => {
    if (!slotList.length) { toast.error("Add at least one time slot"); return; }
    setSlotBusy(true);
    try {
      await api.put("/settings/slots", {
        lead_days: Number(slot.lead_days), max_days: Number(slot.max_days), slots: slotList,
        default_cap: Number(slot.default_cap) || 0,
        caps: slot.caps.filter((c) => c.city.trim() && c.slot).map((c) => ({ city: c.city.trim(), slot: c.slot, cap: Number(c.cap) || 0 })),
      });
      toast.success("Booking slots updated");
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setSlotBusy(false); }
  };

  const saveScheduling = async () => {
    setOpsBusy(true);
    try {
      await api.put("/settings/scheduling", { ops_notification_emails: opsEmails, booking_holidays: [] });
      toast.success("Operations notification emails saved");
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setOpsBusy(false); }
  };

  const togglePA = async (v) => {
    setPaEnabled(v); setPaBusy(true);
    try { await api.put("/settings/partner-availability", { enabled: v }); toast.success(`Partner availability ${v ? "enabled" : "disabled"}`); }
    catch (e) { setPaEnabled(!v); toast.error(apiError(e.response?.data?.detail)); }
    finally { setPaBusy(false); }
  };

  const downloadCapacity = async (template) => {
    try {
      const res = await api.get("/partners/capacity/export", { params: { template }, responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url; a.download = template ? "partner_capacity_template.xlsx" : "partner_capacity.xlsx";
      document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(url);
    } catch (e) { toast.error("Download failed"); }
  };

  const importCapacity = async (e) => {
    const file = e.target.files?.[0]; e.target.value = "";
    if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    try {
      const { data } = await api.post("/partners/capacity/import", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Capacity updated: ${data.partners_updated} partner(s)${data.rows_skipped ? `, ${data.rows_skipped} rows skipped` : ""}`);
      if (data.unknown_partners?.length) toast.warning(`Unknown partners skipped: ${data.unknown_partners.join(", ")}`);
    } catch (err) { toast.error(apiError(err.response?.data?.detail) || "Import failed"); }
  };

  useEffect(() => { api.get("/integrations/gsheet").then((r) => setGToken(r.data.token)).catch(() => {}); }, []);

  const loadDrive = () => api.get("/integrations/gdrive").then((r) => {
    setDrive({ url: r.data.webhook_url || "", token: r.data.token || "", connected: r.data.connected });
    setDriveInput(r.data.webhook_url || "");
  }).catch(() => {});
  const loadMig = () => api.get("/integrations/gdrive/migrate/status").then((r) => setMig(r.data)).catch(() => {});
  useEffect(() => { loadDrive(); loadMig(); }, []);

  const WA_EVENTS = [
    { key: "order_receipt", label: "Order receipt confirmation", hint: "vars: {{1}} name · {{2}} service · {{3}} order no", auth: false },
    { key: "start_otp", label: "Start-job OTP", hint: "vars: {{1}} OTP (Authentication template)", auth: true },
    { key: "end_otp", label: "End-job OTP", hint: "vars: {{1}} OTP (Authentication template)", auth: true },
    { key: "feedback", label: "Feedback / Rate Fixigo", hint: "vars: {{1}} name · {{2}} link", auth: false },
  ];
  const loadWa = () => api.get("/integrations/whatsapp").then((r) => setWa(r.data)).catch(() => {});
  useEffect(() => { loadWa(); }, []);

  const setWaEvent = (key, patch) => setWa((s) => ({ ...s, events: { ...s.events, [key]: { ...s.events[key], ...patch } } }));

  const saveWa = async () => {
    setWaBusy(true);
    try {
      const body = { plugin_id: wa.plugin_id, api_version: wa.api_version, phone_number_id: wa.phone_number_id,
        access_token: wa.access_token || "", events: wa.events };
      const { data } = await api.put("/integrations/whatsapp", body);
      setWa(data);
      toast.success("WhatsApp settings saved");
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setWaBusy(false); }
  };
  const testWa = async () => {
    if (!waTest.phone) { toast.error("Enter a phone number to test"); return; }
    setWaTestBusy(true);
    try {
      await api.post("/integrations/whatsapp/test", waTest);
      toast.success("Test message sent — check WhatsApp");
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setWaTestBusy(false); }
  };

  // Poll migration progress while running
  useEffect(() => {
    if (!mig?.running) return;
    const t = setInterval(loadMig, 2500);
    return () => clearInterval(t);
  }, [mig?.running]);

  const driveScript = `// ===== Fixigo Drive Storage (paste in script.google.com > New Project) =====
const FIXIGO_TOKEN = "${drive.token}";
const ROOT_FOLDER  = "Fixigo CRM";

function doPost(e) {
  try {
    const body = JSON.parse(e.postData.contents);
    if (body.token !== FIXIGO_TOKEN) return _json({ ok: false, error: "Invalid token" });

    if ((body.action || "upload") === "get") {
      const f = DriveApp.getFileById(body.id);
      const blob = f.getBlob();
      return _json({ ok: true, content_type: blob.getContentType(),
                     data_b64: Utilities.base64Encode(blob.getBytes()) });
    }

    const root = _folder(DriveApp.getRootFolder(), ROOT_FOLDER);
    const modFolder = _folder(root, body.module || "Misc");
    const bkFolder  = _folder(modFolder, String(body.booking_id || "general"));
    const bytes = Utilities.base64Decode(body.data_b64);
    const blob  = Utilities.newBlob(bytes, body.content_type || "application/octet-stream", body.filename || "file");
    const file  = bkFolder.createFile(blob);
    file.setSharing(DriveApp.Access.ANYONE_WITH_LINK, DriveApp.Permission.VIEW);
    return _json({ ok: true, id: file.getId(), url: file.getUrl() });
  } catch (err) {
    return _json({ ok: false, error: String(err) });
  }
}

function _folder(parent, name) {
  const it = parent.getFoldersByName(name);
  return it.hasNext() ? it.next() : parent.createFolder(name);
}

function _json(obj) {
  return ContentService.createTextOutput(JSON.stringify(obj))
    .setMimeType(ContentService.MimeType.JSON);
}`;

  const saveDrive = async () => {
    setDriveBusy(true);
    try {
      await api.post("/integrations/gdrive", { webhook_url: driveInput.trim() });
      await loadDrive();
      toast.success(driveInput.trim() ? "Google Drive connected" : "Google Drive disconnected");
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setDriveBusy(false); }
  };

  const testDrive = async () => {
    setDriveBusy(true);
    try {
      const { data } = await api.post("/integrations/gdrive/test");
      toast.success("Test file uploaded to your Drive ✓");
      if (data.url) window.open(data.url, "_blank");
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setDriveBusy(false); }
  };

  const startMigrate = async () => {
    if (!window.confirm("Move all existing CRM files (invoices, media, documents, SOPs, templates) into your Google Drive? Records will point to Drive afterwards.")) return;
    setMigBusy(true);
    try {
      await api.post("/integrations/gdrive/migrate");
      toast.success("Migration started — this runs in the background");
      await loadMig();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setMigBusy(false); }
  };


  const webhookUrl = `${API}/integrations/gsheet/b2b-order`;

  const appsScript = `// ===== Fixigo B2B Order Sync (paste in Extensions > Apps Script) =====
const FIXIGO_WEBHOOK_URL = "${webhookUrl}";
const FIXIGO_TOKEN = "${gToken}";
const SHEET_NAME = "${SHEET_NAME}";
const SYNC_EVERY_MINUTES = 5;   // auto-sync frequency

function onOpen() {
  SpreadsheetApp.getUi().createMenu("Fixigo")
    .addItem("Sync all B2B orders now", "syncAllFixigo")
    .addItem("Enable auto-sync (every " + SYNC_EVERY_MINUTES + " min)", "installFixigoTriggers")
    .addItem("Disable auto-sync", "removeFixigoTriggers")
    .addToUi();
}

// Run this ONCE (from the menu or the editor) to turn on automatic syncing.
function installFixigoTriggers() {
  removeFixigoTriggers();
  ScriptApp.newTrigger("autoSyncFixigo").timeBased().everyMinutes(SYNC_EVERY_MINUTES).create();
  ScriptApp.newTrigger("onEditFixigo").forSpreadsheet(SpreadsheetApp.getActive()).onEdit().create();
  try { SpreadsheetApp.getUi().alert("Auto-sync ON — new orders sync every " + SYNC_EVERY_MINUTES + " minutes."); } catch (e) {}
}

function removeFixigoTriggers() {
  ScriptApp.getProjectTriggers().forEach(function (t) {
    var fn = t.getHandlerFunction();
    if (fn === "autoSyncFixigo" || fn === "onEditFixigo") ScriptApp.deleteTrigger(t);
  });
}

function autoSyncFixigo() { pushAll_(); }          // time-driven (no popup)

function syncAllFixigo() {                          // manual (shows result)
  var msg = pushAll_();
  try { SpreadsheetApp.getUi().alert(msg || "Sync complete."); } catch (e) {}
}

function onEditFixigo(e) {                           // instant sync on edit
  if (!e || !e.range) return;
  var sh = e.range.getSheet();
  if (sh.getName() !== SHEET_NAME || e.range.getRow() === 1) return;
  postRow_(sh, e.range.getRow());
}

function postRow_(sh, row) {
  var lastCol = sh.getLastColumn();
  var headers = sh.getRange(1, 1, 1, lastCol).getValues()[0];
  var values = sh.getRange(row, 1, 1, lastCol).getValues()[0];
  var obj = {};
  headers.forEach(function (h, i) { if (h) obj[String(h).trim()] = values[i]; });
  if (!obj["Partner Order Id"] || !obj["Customer Name"] || !obj["Service Name"]) return;
  UrlFetchApp.fetch(FIXIGO_WEBHOOK_URL, {
    method: "post", contentType: "application/json",
    headers: { "X-Fixigo-Token": FIXIGO_TOKEN },
    payload: JSON.stringify(obj), muteHttpExceptions: true
  });
}

function pushAll_() {
  var sh = SpreadsheetApp.getActiveSpreadsheet().getSheetByName(SHEET_NAME);
  if (!sh) return "Sheet '" + SHEET_NAME + "' not found";
  var data = sh.getDataRange().getValues();
  if (data.length < 2) return "No rows to sync";
  var headers = data[0];
  var orders = [];
  for (var r = 1; r < data.length; r++) {
    var obj = {};
    headers.forEach(function (h, i) { if (h) obj[String(h).trim()] = data[r][i]; });
    if (obj["Partner Order Id"] && obj["Customer Name"]) orders.push(obj);
  }
  if (!orders.length) return "No valid orders found";
  var res = UrlFetchApp.fetch(FIXIGO_WEBHOOK_URL, {
    method: "post", contentType: "application/json",
    headers: { "X-Fixigo-Token": FIXIGO_TOKEN },
    payload: JSON.stringify({ orders: orders }), muteHttpExceptions: true
  });
  return res.getContentText();
}`;

  const copy = (text, label) => {
    navigator.clipboard.writeText(text).then(() => toast.success(`${label} copied`)).catch(() => toast.error("Copy failed"));
  };

  const regenToken = async () => {
    if (!window.confirm("Regenerate the webhook token? Your existing Apps Script will stop working until you paste the new token.")) return;
    setGBusy(true);
    try {
      const { data } = await api.post("/integrations/gsheet/regenerate");
      setGToken(data.token);
      toast.success("New token generated — update your Apps Script");
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setGBusy(false); }
  };

  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.put("/settings/job-limit", { limit: Number(limit) });
      setLimit(data.partner_job_limit);
      toast.success("Settings saved");
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const saveSell = async () => {
    setBusySell(true);
    try {
      const { data } = await api.put("/settings/sell-limit", { limit: Number(sellLimit) });
      setSellLimit(data.partner_sell_limit);
      toast.success("Settings saved");
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setBusySell(false); }
  };

  const saveFees = async () => {
    setBusyFees(true);
    try {
      const { data } = await api.put("/settings/onboarding-fees", { appliance: Number(feeAppliance), cleaning: Number(feeCleaning) });
      setFeeAppliance(data.onboarding_fee_appliance); setFeeCleaning(data.onboarding_fee_cleaning);
      toast.success("Onboarding fees saved");
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setBusyFees(false); }
  };

  return (
    <>
      <PageHeader title="Settings" subtitle="Operational configuration" />
      <div className="p-8 max-w-3xl space-y-6">
        <Card className="rounded-sm p-6 border-border" data-testid="settings-slots">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-sm bg-teal-100 text-teal-700 flex items-center justify-center"><CalendarBlank size={20} weight="bold" /></div>
            <div className="flex-1">
              <h3 className="font-heading font-semibold">Booking Slots</h3>
              <p className="text-sm text-muted-foreground mt-1">Control which dates &amp; time slots customers can book on the storefront. Lead time of 1 = earliest bookable date is tomorrow (T+1).</p>
              <div className="grid grid-cols-2 gap-4 mt-4">
                <div>
                  <Label className="label-caps">Lead time (days)</Label>
                  <Input type="number" min="0" max="30" value={slot.lead_days} onChange={(e) => setSlot({ ...slot, lead_days: e.target.value })} className="rounded-sm mt-1.5" data-testid="slot-lead-days" />
                </div>
                <div>
                  <Label className="label-caps">Booking window (days ahead)</Label>
                  <Input type="number" min="1" max="60" value={slot.max_days} onChange={(e) => setSlot({ ...slot, max_days: e.target.value })} className="rounded-sm mt-1.5" data-testid="slot-max-days" />
                </div>
              </div>
              <div className="mt-4">
                <Label className="label-caps">Time slots (one per line)</Label>
                <textarea className="w-full rounded-sm border border-input mt-1.5 p-3 text-sm min-h-[120px] font-mono" value={slot.slots}
                  onChange={(e) => setSlot({ ...slot, slots: e.target.value })}
                  placeholder={"09:00 - 12:00\n12:00 - 15:00\n15:00 - 18:00\n18:00 - 21:00"} data-testid="slot-slots" />
                <p className="text-xs text-muted-foreground mt-1">Recommended: 3-hour windows.</p>
              </div>

              <div className="mt-5 border-t border-border pt-4">
                <Label className="label-caps">Daily capacity per slot (0 = unlimited)</Label>
                <p className="text-xs text-muted-foreground mt-0.5 mb-2">Caps how many bookings a slot can take per day, so you never oversell a window.</p>
                <Input type="number" min="0" value={slot.default_cap} onChange={(e) => setSlot({ ...slot, default_cap: e.target.value })} className="rounded-sm w-40" data-testid="slot-default-cap" />
                <div className="mt-4">
                  <div className="flex items-center justify-between">
                    <Label className="label-caps">City-specific overrides</Label>
                    <Button variant="outline" size="sm" className="rounded-sm" onClick={addCap} data-testid="slot-add-cap">+ Add override</Button>
                  </div>
                  <div className="mt-2 space-y-2" data-testid="slot-caps-list">
                    {slot.caps.length === 0 && <p className="text-xs text-muted-foreground">No overrides — the default cap applies to every city.</p>}
                    {slot.caps.map((c, i) => (
                      <div key={i} className="flex flex-col sm:flex-row gap-2 items-stretch sm:items-center" data-testid={`slot-cap-row-${i}`}>
                        <Input value={c.city} onChange={(e) => updCap(i, "city", e.target.value)} placeholder="City (e.g. Bengaluru)" className="rounded-sm flex-1" data-testid={`slot-cap-city-${i}`} />
                        <Select value={c.slot} onValueChange={(v) => updCap(i, "slot", v)}>
                          <SelectTrigger className="rounded-sm sm:w-44" data-testid={`slot-cap-slot-${i}`}><SelectValue placeholder="Slot" /></SelectTrigger>
                          <SelectContent>{slotList.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                        </Select>
                        <Input type="number" min="0" value={c.cap} onChange={(e) => updCap(i, "cap", e.target.value)} placeholder="Cap" className="rounded-sm sm:w-24" data-testid={`slot-cap-cap-${i}`} />
                        <Button variant="ghost" size="icon" className="rounded-sm text-destructive shrink-0" onClick={() => rmCap(i)} data-testid={`slot-cap-remove-${i}`}><Trash size={16} /></Button>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <div className="flex justify-end mt-4">
                <Button className="rounded-sm" onClick={saveSlots} disabled={slotBusy} data-testid="slots-save">{slotBusy ? "Saving…" : "Save Slots"}</Button>
              </div>
            </div>
          </div>
        </Card>

        <Card className="rounded-sm p-6 border-border" data-testid="settings-ops-emails">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-sm bg-amber-100 text-amber-700 flex items-center justify-center"><CalendarBlank size={20} weight="bold" /></div>
            <div className="flex-1">
              <h3 className="font-heading font-semibold">Operations Notification Emails</h3>
              <p className="text-sm text-muted-foreground mt-1">Who gets notified when a customer submits a preferred schedule via the scheduling link. Separate multiple emails with commas. Leave blank to fall back to the support inbox.</p>
              <Input value={opsEmails} onChange={(e) => setOpsEmails(e.target.value)} placeholder="ops1@fixigo.in, ops2@fixigo.in" className="rounded-sm mt-3" data-testid="ops-emails-input" />
              <div className="flex justify-end mt-4">
                <Button className="rounded-sm" onClick={saveScheduling} disabled={opsBusy} data-testid="ops-emails-save">{opsBusy ? "Saving…" : "Save"}</Button>
              </div>
            </div>
          </div>
        </Card>

        <Card className="rounded-sm p-6 border-border" data-testid="settings-partner-availability">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-sm bg-purple-100 text-purple-700 flex items-center justify-center"><UsersThree size={20} weight="bold" /></div>
            <div className="flex-1">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <h3 className="font-heading font-semibold">Partner Availability Management</h3>
                  <p className="text-sm text-muted-foreground mt-1">When ON, the scheduling page only shows slots that still have partner capacity left in the customer's pincode. When OFF, slot availability is unrestricted (current behaviour).</p>
                </div>
                <Switch checked={paEnabled} disabled={paBusy} onCheckedChange={togglePA} data-testid="partner-availability-toggle" />
              </div>
              <div className="mt-4 border-t border-border pt-4">
                <Label className="label-caps">Per-partner slot capacity (per pincode)</Label>
                <p className="text-xs text-muted-foreground mt-0.5 mb-3">Download the template (rows for every partner × their serviceable pincode, one column per booking slot), fill the numeric capacity per slot (0/blank = not serving that slot), and re-import. Export includes current values and is re-importable.</p>
                <div className="flex flex-wrap gap-2">
                  <Button variant="outline" size="sm" className="rounded-sm gap-1.5" onClick={() => downloadCapacity(true)} data-testid="capacity-template-btn"><CloudArrowUp size={14} /> Download Template</Button>
                  <Button variant="outline" size="sm" className="rounded-sm gap-1.5" onClick={() => downloadCapacity(false)} data-testid="capacity-export-btn"><Copy size={14} /> Export Current</Button>
                  <label>
                    <input type="file" accept=".xlsx,.xls" className="hidden" onChange={importCapacity} data-testid="capacity-import-input" />
                    <Button asChild size="sm" className="rounded-sm gap-1.5 cursor-pointer" data-testid="capacity-import-btn"><span><ArrowsClockwise size={14} /> Import Capacity</span></Button>
                  </label>
                </div>
              </div>
            </div>
          </div>
        </Card>


        <Card className="rounded-sm p-6 border-border" data-testid="settings-job-limit">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-sm bg-blue-100 text-blue-700 flex items-center justify-center"><UsersThree size={20} weight="bold" /></div>
            <div className="flex-1">
              <h3 className="font-heading font-semibold">Partner Service Ticket Limit</h3>
              <p className="text-sm text-muted-foreground mt-1">Maximum active service tickets (assigned / accepted / in-progress) a partner can hold before auto-assignment skips them. Can be overridden per partner.</p>
              <div className="flex items-end gap-3 mt-4">
                <div>
                  <Label className="label-caps">Max active service tickets / partner</Label>
                  <Input type="number" min={1} max={100} value={limit} onChange={(e) => setLimit(e.target.value)} className="rounded-sm mt-1.5 w-32" data-testid="job-limit-input" />
                </div>
                <Button className="rounded-sm" onClick={save} disabled={busy} data-testid="job-limit-save">{busy ? "Saving…" : "Save"}</Button>
              </div>
            </div>
          </div>
        </Card>

        <Card className="rounded-sm p-6 border-border" data-testid="settings-sell-limit">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-sm bg-emerald-100 text-emerald-700 flex items-center justify-center"><DeviceMobile size={20} weight="bold" /></div>
            <div className="flex-1">
              <h3 className="font-heading font-semibold">Partner Sell Device Pickup Limit</h3>
              <p className="text-sm text-muted-foreground mt-1">Maximum active device pickups a Sell-enabled partner can hold before auto-assignment skips them. Can be overridden per partner.</p>
              <div className="flex items-end gap-3 mt-4">
                <div>
                  <Label className="label-caps">Max active sell pickups / partner</Label>
                  <Input type="number" min={1} max={100} value={sellLimit} onChange={(e) => setSellLimit(e.target.value)} className="rounded-sm mt-1.5 w-32" data-testid="sell-limit-input" />
                </div>
                <Button className="rounded-sm" onClick={saveSell} disabled={busySell} data-testid="sell-limit-save">{busySell ? "Saving…" : "Save"}</Button>
              </div>
            </div>
          </div>
        </Card>

        <Card className="rounded-sm p-6 border-border" data-testid="settings-onboarding-fees">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-sm bg-amber-100 text-amber-700 flex items-center justify-center"><CurrencyInr size={20} weight="bold" /></div>
            <div className="flex-1">
              <h3 className="font-heading font-semibold">Partner Onboarding Fee</h3>
              <p className="text-sm text-muted-foreground mt-1">One-time onboarding fee shown on the Become-a-Partner form. Appliance-repair tier applies when any AC/Refrigerator/Washing Machine/TV (or non-cleaning) service is chosen; the cleaning tier applies for cleaning-only partners. Admin can also send a custom link per applicant.</p>
              <div className="flex items-end gap-3 mt-4 flex-wrap">
                <div>
                  <Label className="label-caps">Appliance repair (₹)</Label>
                  <Input type="number" min={0} value={feeAppliance} onChange={(e) => setFeeAppliance(e.target.value)} className="rounded-sm mt-1.5 w-32" data-testid="fee-appliance-input" />
                </div>
                <div>
                  <Label className="label-caps">Home cleaning (₹)</Label>
                  <Input type="number" min={0} value={feeCleaning} onChange={(e) => setFeeCleaning(e.target.value)} className="rounded-sm mt-1.5 w-32" data-testid="fee-cleaning-input" />
                </div>
                <Button className="rounded-sm" onClick={saveFees} disabled={busyFees} data-testid="fees-save">{busyFees ? "Saving…" : "Save"}</Button>
              </div>
            </div>
          </div>
        </Card>

        <Card className="rounded-sm p-6 border-border" data-testid="settings-whatsapp">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-sm bg-green-100 text-green-700 flex items-center justify-center"><WhatsappLogo size={20} weight="bold" /></div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <h3 className="font-heading font-semibold">WhatsApp Notifications (ChakraHQ)</h3>
                {wa?.connected && <span className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-sm px-2 py-0.5" data-testid="wa-connected-badge"><CheckCircle size={13} weight="fill" /> Connected</span>}
              </div>
              <p className="text-sm text-muted-foreground mt-1">Send order confirmation, start/end OTP and feedback requests to customers over WhatsApp. Live delivery uses your <strong>approved</strong> ChakraHQ templates. Get these values from your ChakraHQ dashboard.</p>

              {wa && (
                <>
                  <div className="grid sm:grid-cols-2 gap-3 mt-4">
                    <div><Label className="label-caps">Plugin ID</Label>
                      <Input value={wa.plugin_id} onChange={(e) => setWa({ ...wa, plugin_id: e.target.value })} className="rounded-sm mt-1.5 font-mono text-xs" data-testid="wa-plugin-id" /></div>
                    <div><Label className="label-caps">Phone Number ID</Label>
                      <Input value={wa.phone_number_id} onChange={(e) => setWa({ ...wa, phone_number_id: e.target.value })} className="rounded-sm mt-1.5 font-mono text-xs" data-testid="wa-phone-id" /></div>
                    <div><Label className="label-caps">API Version</Label>
                      <Input value={wa.api_version} onChange={(e) => setWa({ ...wa, api_version: e.target.value })} placeholder="v22.0" className="rounded-sm mt-1.5 font-mono text-xs" data-testid="wa-api-version" /></div>
                    <div><Label className="label-caps">Access Token</Label>
                      <Input type="password" value={wa.access_token || ""} onChange={(e) => setWa({ ...wa, access_token: e.target.value })} placeholder={wa.has_token ? "•••••• (saved — leave blank to keep)" : "Paste ChakraHQ access token"} className="rounded-sm mt-1.5 font-mono text-xs" data-testid="wa-token" /></div>
                  </div>

                  <div className="mt-5 space-y-3">
                    <Label className="label-caps">Message templates</Label>
                    {WA_EVENTS.map((ev) => (
                      <div key={ev.key} className="border border-border rounded-sm p-3" data-testid={`wa-event-${ev.key}`}>
                        <div className="flex items-center justify-between gap-2">
                          <div className="min-w-0">
                            <p className="text-sm font-medium">{ev.label}</p>
                            <p className="text-[11px] text-muted-foreground">{ev.hint}</p>
                          </div>
                          <Switch checked={!!wa.events[ev.key]?.enabled} onCheckedChange={(v) => setWaEvent(ev.key, { enabled: v })} data-testid={`wa-enable-${ev.key}`} />
                        </div>
                        <div className="grid grid-cols-2 gap-2 mt-2">
                          <Input value={wa.events[ev.key]?.template || ""} onChange={(e) => setWaEvent(ev.key, { template: e.target.value })} placeholder="Template name" className="rounded-sm text-xs" data-testid={`wa-template-${ev.key}`} />
                          <Input value={wa.events[ev.key]?.lang || ""} onChange={(e) => setWaEvent(ev.key, { lang: e.target.value })} placeholder="Language (e.g. en_US)" className="rounded-sm text-xs" data-testid={`wa-lang-${ev.key}`} />
                        </div>
                        {ev.auth && (
                          <label className="flex items-center gap-2 mt-2 text-xs text-muted-foreground cursor-pointer">
                            <Switch checked={!!wa.events[ev.key]?.auth} onCheckedChange={(v) => setWaEvent(ev.key, { auth: v })} className="scale-90" data-testid={`wa-auth-${ev.key}`} />
                            Authentication template (adds copy-code button). Turn off if using a plain text template.
                          </label>
                        )}
                      </div>
                    ))}
                  </div>

                  <div className="flex items-center gap-2 mt-4">
                    <Button className="rounded-sm" onClick={saveWa} disabled={waBusy} data-testid="wa-save">{waBusy ? "Saving…" : "Save WhatsApp Settings"}</Button>
                  </div>

                  <div className="mt-4 rounded-sm bg-secondary/50 border border-border p-3">
                    <Label className="label-caps">Send a test</Label>
                    <div className="flex flex-col sm:flex-row gap-2 mt-1.5">
                      <Input value={waTest.phone} onChange={(e) => setWaTest({ ...waTest, phone: e.target.value })} placeholder="Phone (10-digit)" className="rounded-sm" data-testid="wa-test-phone" />
                      <Select value={waTest.event} onValueChange={(v) => setWaTest({ ...waTest, event: v })}>
                        <SelectTrigger className="rounded-sm sm:w-56" data-testid="wa-test-event"><SelectValue /></SelectTrigger>
                        <SelectContent>{WA_EVENTS.map((ev) => <SelectItem key={ev.key} value={ev.key}>{ev.label}</SelectItem>)}</SelectContent>
                      </Select>
                      <Button variant="outline" className="rounded-sm gap-2 shrink-0" onClick={testWa} disabled={waTestBusy} data-testid="wa-test-send"><PaperPlaneTilt size={15} /> {waTestBusy ? "Sending…" : "Send test"}</Button>
                    </div>
                    <p className="text-[11px] text-muted-foreground mt-2">Test uses OTP <span className="font-mono">123456</span> / sample data. It only succeeds once that template is approved in ChakraHQ.</p>
                  </div>
                </>
              )}
            </div>
          </div>
        </Card>

        <Card className="rounded-sm p-6 border-border" data-testid="settings-gdrive">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-sm bg-sky-100 text-sky-700 flex items-center justify-center"><CloudArrowUp size={20} weight="bold" /></div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <h3 className="font-heading font-semibold">File & Media Storage → Google Drive</h3>
                {drive.connected && <span className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-sm px-2 py-0.5" data-testid="gdrive-connected-badge"><CheckCircle size={13} weight="fill" /> Connected</span>}
              </div>
              <p className="text-sm text-muted-foreground mt-1">Send every upload (invoices, sell-device photos/videos, partner documents, SOP files, generated documents) straight to your own Google Drive — no Google Cloud Console needed. When connected, new files are stored in Drive and CRM links open the Drive view (external viewers get read-only access).</p>

              <div className="rounded-sm bg-secondary/50 border border-border p-3 text-xs text-muted-foreground space-y-1 mt-4">
                <p className="font-semibold text-foreground">Setup (one-time):</p>
                <p>1. Go to <span className="font-mono">script.google.com</span> → <span className="font-semibold">New Project</span>.</p>
                <p>2. Delete any starter code, paste the script below, and <span className="font-semibold">Save</span>.</p>
                <p>3. Click <span className="font-semibold">Deploy → New deployment</span> → type <span className="font-semibold">Web app</span>. Set <span className="font-semibold">Execute as: Me</span> and <span className="font-semibold">Who has access: Anyone</span> → Deploy &amp; authorize.</p>
                <p>4. Copy the <span className="font-semibold">Web app URL</span> (ends in <span className="font-mono">/exec</span>) and paste it below, then Save.</p>
                <p>Files land in a <span className="font-mono">Fixigo CRM</span> folder, organised by module &amp; booking.</p>
              </div>

              <div className="mt-4 space-y-3">
                <div>
                  <Label className="label-caps">Secret Token (already baked into the script below)</Label>
                  <div className="flex gap-2 mt-1.5">
                    <Input readOnly value={drive.token} className="rounded-sm font-mono text-xs" data-testid="gdrive-token" />
                    <Button variant="outline" size="icon" className="rounded-sm shrink-0" onClick={() => copy(drive.token, "Token")} data-testid="gdrive-copy-token"><Copy size={16} /></Button>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Button className="rounded-sm gap-2" onClick={() => copy(driveScript, "Apps Script")} data-testid="gdrive-copy-script"><Copy size={16} /> Copy Apps Script</Button>
                </div>
                <div>
                  <Label className="label-caps">Web App URL</Label>
                  <div className="flex gap-2 mt-1.5">
                    <Input value={driveInput} onChange={(e) => setDriveInput(e.target.value)} placeholder="https://script.google.com/macros/s/…/exec" className="rounded-sm font-mono text-xs" data-testid="gdrive-url-input" />
                    <Button className="rounded-sm shrink-0" onClick={saveDrive} disabled={driveBusy} data-testid="gdrive-save">{driveBusy ? "Saving…" : "Save"}</Button>
                  </div>
                </div>
                {drive.connected && (
                  <div className="flex flex-wrap items-center gap-2">
                    <Button variant="outline" size="sm" className="rounded-sm gap-2" onClick={testDrive} disabled={driveBusy} data-testid="gdrive-test"><Plugs size={15} /> Test connection</Button>
                    <Button variant="outline" size="sm" className="rounded-sm gap-2" onClick={startMigrate} disabled={migBusy || mig?.running} data-testid="gdrive-migrate"><ArrowsClockwise size={15} /> {mig?.running ? "Migrating…" : "Migrate existing files"}</Button>
                  </div>
                )}
                {mig && (mig.running || mig.done > 0 || mig.failed > 0) && (
                  <div className="rounded-sm border border-border bg-secondary/40 p-3 text-xs" data-testid="gdrive-migrate-status">
                    <p className="font-semibold text-foreground">{mig.running ? "Migration in progress…" : "Last migration"}</p>
                    <p className="mt-1">Moved: <span className="font-semibold text-emerald-700">{mig.done || 0}</span>{(mig.failed > 0) && <> · Failed: <span className="font-semibold text-destructive">{mig.failed}</span></>}</p>
                    {mig.errors?.length > 0 && <p className="mt-1 text-destructive break-words">{mig.errors.slice(0, 3).join(" · ")}</p>}
                  </div>
                )}
              </div>
            </div>
          </div>
        </Card>

        <Card className="rounded-sm p-6 border-border" data-testid="settings-gsheet">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-sm bg-violet-100 text-violet-700 flex items-center justify-center"><Plugs size={20} weight="bold" /></div>
            <div className="flex-1 min-w-0">
              <h3 className="font-heading font-semibold">Google Sheet → CRM (B2B Orders)</h3>
              <p className="text-sm text-muted-foreground mt-1">Auto-create B2B jobs when a new row is added to your Google Sheet tab named <span className="font-mono font-semibold">{SHEET_NAME}</span>. Duplicate orders are skipped automatically (matched by <span className="font-semibold">Partner Order Id</span>).</p>

            <div className="mt-4 space-y-3">
              <div>
                <Label className="label-caps">Webhook URL</Label>
                <div className="flex gap-2 mt-1.5">
                  <Input readOnly value={webhookUrl} className="rounded-sm font-mono text-xs" data-testid="gsheet-url" />
                  <Button variant="outline" size="icon" className="rounded-sm shrink-0" onClick={() => copy(webhookUrl, "Webhook URL")} data-testid="gsheet-copy-url"><Copy size={16} /></Button>
                </div>
              </div>
              <div>
                <Label className="label-caps">Secret Token</Label>
                <div className="flex gap-2 mt-1.5">
                  <Input readOnly value={gToken} className="rounded-sm font-mono text-xs" data-testid="gsheet-token" />
                  <Button variant="outline" size="icon" className="rounded-sm shrink-0" onClick={() => copy(gToken, "Token")} data-testid="gsheet-copy-token"><Copy size={16} /></Button>
                  <Button variant="outline" size="icon" className="rounded-sm shrink-0" onClick={regenToken} disabled={gBusy} title="Regenerate token" data-testid="gsheet-regen"><ArrowsClockwise size={16} /></Button>
                </div>
                <p className="text-xs text-muted-foreground mt-1">Keep this secret. Regenerating it will disable the old Apps Script until updated.</p>
              </div>

              <div className="rounded-sm bg-secondary/50 border border-border p-3 text-xs text-muted-foreground space-y-1">
                <p className="font-semibold text-foreground">Setup (one-time):</p>
                <p>1. In your tracker, name the orders tab exactly <span className="font-mono">{SHEET_NAME}</span> with the same headers as the B2B import template.</p>
                <p>2. Open <span className="font-semibold">Extensions → Apps Script</span>, paste the script below, and Save.</p>
                <p>3. <span className="font-semibold">Reload the spreadsheet</span> — a new <span className="font-semibold">Fixigo</span> menu appears in the top bar.</p>
                <p>4. Click <span className="font-semibold">Fixigo → Enable auto-sync (every 5 min)</span> and authorize once. That's it — orders now sync automatically, no refresh needed.</p>
                <p className="text-foreground/80">New rows also sync instantly on edit. Use <span className="font-semibold">Fixigo → Sync all</span> for a one-time backfill, or <span className="font-semibold">Disable auto-sync</span> to stop. Re-syncing is safe — duplicate Order Ids are skipped automatically.</p>
              </div>

              <div className="flex items-center gap-2">
                <Button className="rounded-sm gap-2" onClick={() => copy(appsScript, "Apps Script")} data-testid="gsheet-copy-script"><Copy size={16} /> Copy Google Apps Script</Button>
              </div>
            </div>
            </div>
          </div>
        </Card>
      </div>
    </>
  );
}